import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import tokenService from '../services/tokenService';

const AuthContext = createContext();

// أدوار المستخدمين
export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN', 
  MANAGER: 'MANAGER',
  SUPERVISOR: 'SUPERVISOR',
  TRAINER: 'TRAINER',
  STUDENT: 'STUDENT'
};

// صلاحيات الأدوار
export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    'manage_system',
    'manage_users',
    'manage_reports',
    'manage_settings',
    'view_all_data'
  ],
  [USER_ROLES.ADMIN]: [
    'manage_users',
    'manage_reports', 
    'manage_settings',
    'view_all_data'
  ],
  [USER_ROLES.MANAGER]: [
    'manage_employees',
    'view_reports',
    'manage_trainers'
  ],
  [USER_ROLES.SUPERVISOR]: [
    'view_reports',
    'view_statistics',
    'manage_students'
  ],
  [USER_ROLES.TRAINER]: [
    'manage_students',
    'manage_competitions',
    'view_student_progress'
  ],
  [USER_ROLES.STUDENT]: [
    'access_training',
    'view_progress',
    'participate_competitions'
  ]
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // تحقق من حالة المصادقة عند تحميل التطبيق
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = tokenService.getAccessToken();
      if (token) {
        // التحقق من انتهاء صلاحية التوكن
        if (tokenService.isTokenExpired(token)) {
          try {
            await tokenService.refreshAccessToken();
          } catch (refreshError) {
            console.error('Token refresh failed');
            tokenService.clearTokens();
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            return;
          }
        }

        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      // في حالة فشل التحقق من المصادقة، نمسح البيانات فقط في حالات محددة
      if (error.response?.status === 401) {
        // انتهاء صلاحية التوكن
        tokenService.clearTokens();
        setUser(null);
        setIsAuthenticated(false);
      } else if (error.message === 'No token found') {
        // لا يوجد توكن
        setUser(null);
        setIsAuthenticated(false);
      } else if (error.message === 'Incomplete user data') {
        // بيانات المستخدم غير مكتملة - نحاول مرة أخرى
        // لا نمسح التوكن، قد تكون مشكلة مؤقتة
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.login(credentials);
      
      if (response.success) {
        if (response.access_token && response.refresh_token) {
          tokenService.setTokens(response.access_token, response.refresh_token);
        }
        
        // استدعاء /auth/me للحصول على البيانات الكاملة بما فيها الصلاحيات
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true, user: userData };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: 'Network connection error' 
      };
    } finally {
      // تأخير بسيط لضمان عرض الرسالة
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // مسح جميع التوكنات
      tokenService.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // دالة لمعالجة انتهاء صلاحية الجلسة
  const handleSessionExpired = () => {
    tokenService.clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    // يمكن إضافة إشعار للمستخدم هنا
  };

  // دالة لإعادة المحاولة
  const retryAuthCheck = async () => {
    const token = tokenService.getAccessToken();
    if (token) {
      try {
        setLoading(true);
        
        // التحقق من انتهاء صلاحية التوكن ومحاولة التجديد
        if (tokenService.isTokenExpired(token)) {
          try {
            await tokenService.refreshAccessToken();
          } catch (refreshError) {
            console.error('Token refresh failed during retry:', refreshError);
            tokenService.clearTokens();
            setUser(null);
            setIsAuthenticated(false);
            setLoading(false);
            return false;
          }
        }
        
        const userData = await authService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      } catch (error) {
        console.error('Retry auth check failed:', error);
        return false;
      } finally {
        setLoading(false);
      }
    }
    return false;
  };

  const hasPermission = (permission) => {
    if (!user || !permission) return false;
    const perms = user.permissions;
    if (!Array.isArray(perms)) return false;
    return perms.includes(permission);
  };

  const hasAnyPermission = (permissions) => {
    if (!user || !Array.isArray(permissions) || permissions.length === 0) return false;
    const userPerms = user.permissions || [];
    return permissions.some((p) => userPerms.includes(p));
  };

  const hasAllPermissions = (permissions) => {
    if (!user || !Array.isArray(permissions) || permissions.length === 0) return false;
    const userPerms = user.permissions || [];
    return permissions.every((p) => userPerms.includes(p));
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    handleSessionExpired,
    retryAuthCheck,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    USER_ROLES,
    ROLE_PERMISSIONS
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth يجب استخدامه داخل AuthProvider');
  }
  return context;
};
